/**
 * Passkey client service.
 *
 * Feature folder: documentation/features/passkeys/
 * Plan doc:       documentation/features/passkeys/plan.md
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS FILE EXISTS
 * ─────────────────────────────────────────────────────────────────────────────
 *   Supabase Auth does not yet ship a native passkey API in hosted projects
 *   (as of 2026-04-23). supabase-js PR #2263 is adding exactly this surface
 *   — see plan.md §2.2. Until that lands we implement a thin shim that
 *   talks to our own Edge Functions but exposes the **same public API** as
 *   the upcoming native one.
 *
 *   When PR #2263 ships we flip each method's body to the native call and
 *   delete the Edge Functions. No UI code needs to change.
 *
 *   The API surface below mirrors the PR:
 *     supabase.auth.signInWithPasskey()
 *     supabase.auth.registerPasskey()
 *     supabase.auth.passkey.{start,verify}Registration
 *     supabase.auth.passkey.{start,verify}Authentication
 *     supabase.auth.passkey.{list,update,delete}
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * DO NOT import Edge Function internals from UI code. Always go through
 * the methods exported here (or usePasskeys in '@packages/supabase/hooks').
 * ─────────────────────────────────────────────────────────────────────────────
 */

import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration
} from '@simplewebauthn/browser'
import type { Session, SupabaseClient } from '@supabase/supabase-js'

// -----------------------------------------------------------------------------
// Types — kept deliberately compatible with @simplewebauthn/types.
// -----------------------------------------------------------------------------

export interface PasskeySummary {
  id: string
  /** base64url-encoded credential id */
  credentialId: string
  transports: string[] | null
  deviceName: string | null
  backedUp: boolean
  aaguid: string | null
  createdAt: string
  lastUsedAt: string | null
}

export interface RegisterPasskeyOptions {
  deviceName?: string
}

export interface SignInWithPasskeyOptions {
  /** Optional email hint — limits the OS credential picker. */
  email?: string
}

export interface PasskeyServiceError extends Error {
  code: string
}

function toError(code: string, message: string): PasskeyServiceError {
  const err = new Error(message) as PasskeyServiceError
  err.code = code
  return err
}

/**
 * Translate a WebAuthn DOMException into a stable error code.
 *
 * `NotAllowedError` is the one we treat as a user cancellation (that's what
 * Chromium/WebKit throw when the user dismisses the prompt OR when the call
 * times out). Everything else surfaces as `webauthn_<DOMException.name>` so
 * the UI can distinguish between "user changed their mind" and "your RP is
 * misconfigured" — the previous blanket `canceled` mapping silently hid
 * SecurityError / InvalidStateError / AbortError and made debugging painful.
 */
function translateWebAuthnError(e: unknown, stage: string): PasskeyServiceError {
  const err = e as Error & { name?: string }
  const name = err?.name || 'Error'
  // Always log so devs have something to look at in the console even when
  // the UI swallows cancellations.
  // eslint-disable-next-line no-console
  console.warn(`[passkey] ${stage} failed: ${name}`, err)
  const code = name === 'NotAllowedError' ? 'canceled' : `webauthn_${name}`
  return toError(code, err?.message || `Passkey ${stage} failed`)
}

// -----------------------------------------------------------------------------
// Capabilities
// -----------------------------------------------------------------------------

export function isPasskeySupported(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return browserSupportsWebAuthn()
  } catch {
    return false
  }
}

// -----------------------------------------------------------------------------
// Main API
// -----------------------------------------------------------------------------

export function createPasskeyService(supabase: SupabaseClient) {
  // ---- internal: Edge Function invoker -------------------------------------
  async function invoke<T>(fn: string, body?: unknown): Promise<T> {
    const { data, error } = await supabase.functions.invoke(fn, { body })
    if (error) {
      const msg = (error as Error).message || 'Edge Function failed'
      throw toError('edge_fn_failed', msg)
    }
    const result = data as { ok: boolean; code?: string; message?: string } & T
    if (!result?.ok) {
      throw toError(result?.code ?? 'unknown', result?.message ?? 'Passkey request failed')
    }
    return result as T
  }

  // --------------------------------------------------------------------------
  // Registration (authenticated user)
  // --------------------------------------------------------------------------

  /**
   * High-level: start + finish a registration ceremony.
   * Mirrors the planned `supabase.auth.registerPasskey()`.
   */
  async function registerPasskey(opts: RegisterPasskeyOptions = {}): Promise<void> {
    if (!isPasskeySupported()) {
      throw toError('unsupported', 'Passkeys are not supported on this device')
    }

    const { options, deviceName } = await invoke<{
      options: Parameters<typeof startRegistration>[0]['optionsJSON']
      deviceName: string | null
    }>('passkey-register-begin', { deviceName: opts.deviceName })

    let attResp
    try {
      attResp = await startRegistration({ optionsJSON: options })
    } catch (e) {
      throw translateWebAuthnError(e, 'registration')
    }

    await invoke('passkey-register-complete', {
      response: attResp,
      deviceName: opts.deviceName ?? deviceName ?? guessDeviceName()
    })
  }

  // --------------------------------------------------------------------------
  // Sign in (unauthenticated) — magic-link bridge
  // --------------------------------------------------------------------------

  /**
   * High-level: start + finish an authentication ceremony, then redeem
   * the returned token_hash for a real Supabase session.
   *
   * Mirrors the planned `supabase.auth.signInWithPasskey()`.
   */
  async function signInWithPasskey(
    opts: SignInWithPasskeyOptions = {}
  ): Promise<Session | null> {
    if (!isPasskeySupported()) {
      throw toError('unsupported', 'Passkeys are not supported on this device')
    }

    const { options } = await invoke<{
      options: Parameters<typeof startAuthentication>[0]['optionsJSON']
    }>('passkey-auth-begin', opts.email ? { email: opts.email } : {})

    let asseResp
    try {
      asseResp = await startAuthentication({ optionsJSON: options })
    } catch (e) {
      throw translateWebAuthnError(e, 'authentication')
    }

    const result = await invoke<{ email: string; token_hash: string }>(
      'passkey-auth-complete',
      { response: asseResp }
    )

    // Redeem the hashed token for a Supabase session.
    // `verifyOtp({ type: 'email', token_hash })` is what magic-link hashed
    // tokens expect in supabase-js v2.
    const { data, error } = await supabase.auth.verifyOtp({
      type: 'email',
      token_hash: result.token_hash
    })
    if (error) throw toError('session_exchange_failed', error.message)

    return data.session ?? null
  }

  // --------------------------------------------------------------------------
  // Step-up (authenticated) — stamps app_metadata.last_passkey_uv_at
  // --------------------------------------------------------------------------

  async function stepUpWithPasskey(): Promise<{ lastPasskeyUvAt: string }> {
    if (!isPasskeySupported()) {
      throw toError('unsupported', 'Passkeys are not supported on this device')
    }

    const { options } = await invoke<{
      options: Parameters<typeof startAuthentication>[0]['optionsJSON']
    }>('passkey-stepup-begin', {})

    let asseResp
    try {
      asseResp = await startAuthentication({ optionsJSON: options })
    } catch (e) {
      throw translateWebAuthnError(e, 'step-up')
    }

    const result = await invoke<{ last_passkey_uv_at: string }>(
      'passkey-stepup-complete',
      { response: asseResp }
    )

    // Refresh the session so the new app_metadata claim ships inside the JWT.
    await supabase.auth.refreshSession()

    return { lastPasskeyUvAt: result.last_passkey_uv_at }
  }

  // --------------------------------------------------------------------------
  // Management (RLS-protected direct table access)
  // --------------------------------------------------------------------------

  async function list(): Promise<PasskeySummary[]> {
    const { data, error } = await supabase
      .from('webauthn_credentials_public')
      .select('*')
      .order('created_at', { ascending: false })
    if (error) throw toError('list_failed', error.message)
    return (data ?? []).map((row: Passkey) => ({
      id: row.id,
      credentialId: row.credential_id_b64,
      transports: row.transports,
      deviceName: row.device_name,
      backedUp: row.backed_up,
      aaguid: row.aaguid,
      createdAt: row.created_at,
      lastUsedAt: row.last_used_at
    }))
  }

  async function rename(id: string, deviceName: string): Promise<void> {
    const { error } = await supabase
      .from('webauthn_credentials')
      .update({ device_name: deviceName })
      .eq('id', id)
    if (error) throw toError('rename_failed', error.message)
  }

  async function remove(id: string): Promise<void> {
    const { error } = await supabase
      .from('webauthn_credentials')
      .delete()
      .eq('id', id)
    if (error) throw toError('delete_failed', error.message)
  }

  // --------------------------------------------------------------------------
  // Step-up freshness helper
  // --------------------------------------------------------------------------

  /**
   * Returns true when the current session has a recent passkey step-up
   * within the given window. Useful for gating sensitive UI.
   */
  async function hasFreshStepUp(windowMs = 5 * 60 * 1000): Promise<boolean> {
    const { data } = await supabase.auth.getSession()
    const ts = data.session?.user?.app_metadata?.last_passkey_uv_at as
      | string
      | undefined
    if (!ts) return false
    const when = Date.parse(ts)
    if (Number.isNaN(when)) return false
    return Date.now() - when < windowMs
  }

  // --------------------------------------------------------------------------
  // Public surface — deliberately mirrors supabase-js PR #2263.
  // --------------------------------------------------------------------------

  return {
    // High-level:
    registerPasskey,
    signInWithPasskey,
    stepUpWithPasskey,
    // Management:
    passkey: {
      list,
      update: rename,
      delete: remove
    },
    // Helpers:
    hasFreshStepUp,
    isPasskeySupported
  }
}

export type PasskeyService = ReturnType<typeof createPasskeyService>

// -----------------------------------------------------------------------------
// Misc
// -----------------------------------------------------------------------------

function guessDeviceName(): string {
  if (typeof navigator === 'undefined') return 'Passkey'
  const ua = navigator.userAgent
  if (/iPhone/i.test(ua)) return 'iPhone'
  if (/iPad/i.test(ua)) return 'iPad'
  if (/Android/i.test(ua)) return 'Android device'
  if (/Macintosh/i.test(ua)) return 'Mac'
  if (/Windows/i.test(ua)) return 'Windows device'
  if (/Linux/i.test(ua)) return 'Linux device'
  return 'Passkey'
}
